/**
 * Refresh Stage 6: Refresh Brief
 *
 * LLM stage that synthesizes audit, SERP, competitors, and links into
 * a single actionable brief. Same pattern as creation Stage3aBriefV2.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

const ACTION_COLORS: Record<string, string> = {
  keep: 'bg-gray-100 text-gray-600',
  update: 'bg-blue-100 text-blue-700',
  rewrite: 'bg-amber-100 text-amber-700',
  new: 'bg-green-100 text-green-700',
};

function InputSummary() {
  const { document } = useArticleContext();
  const audit = (document as any)?.refresh?.audit;
  const hasSerp = Boolean(document?.targeting?.serp?.overview);
  const hasAnalysis = Boolean((document?.targeting?.competitors as any)?.analysis);
  const hasLinks = (document?.content_research?.internal_link_candidates || []).length > 0;

  return (
    <div className="text-xs text-gray-600 space-y-1 bg-gray-50 rounded border p-2">
      <div>
        Audit: {audit?.strategy || '—'}
        {audit && <span className="text-green-600 ml-1">&#10003;</span>}
      </div>
      <div>
        SERP: {hasSerp ? (document?.targeting?.serp as any)?.overview?.decision : '—'}
        {hasSerp && <span className="text-green-600 ml-1">&#10003;</span>}
      </div>
      <div>
        Competitor analysis: {hasAnalysis ? 'Complete' : '—'}
        {hasAnalysis && <span className="text-green-600 ml-1">&#10003;</span>}
      </div>
      <div>
        Internal links: {hasLinks ? `${document!.content_research!.internal_link_candidates!.length} candidates` : '—'}
        {hasLinks && <span className="text-green-600 ml-1">&#10003;</span>}
      </div>
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const brief = (doc as any)?.refresh?.brief;
  if (!brief?.sections || brief.sections.length === 0) {
    return <div className="text-sm text-gray-500">No brief generated yet.</div>;
  }

  const meta = brief.meta || {};
  const keepCount = brief.sections.filter((s: any) => s.action === 'keep').length;
  const updateCount = brief.sections.filter((s: any) => s.action === 'update').length;
  const rewriteCount = brief.sections.filter((s: any) => s.action === 'rewrite').length;
  const newCount = brief.sections.filter((s: any) => s.action === 'new').length;

  return (
    <div className="space-y-3">
      {/* Meta */}
      {meta.title && (
        <div>
          <div className="text-xs font-medium text-gray-700">Meta</div>
          <div className="text-xs text-gray-600">
            {meta.title} | /{meta.slug}
          </div>
          {meta.target_word_count > 0 && (
            <div className="text-xs text-gray-400">Target: {meta.target_word_count} words</div>
          )}
        </div>
      )}

      {/* Narrative arc */}
      {brief.narrative_arc && (
        <div>
          <div className="text-xs font-medium text-gray-700">Narrative arc</div>
          <div className="text-xs text-gray-600">{brief.narrative_arc}</div>
        </div>
      )}

      {/* Action summary */}
      <div className="flex gap-2 text-[10px]">
        {keepCount > 0 && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{keepCount} keep</span>}
        {updateCount > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{updateCount} update</span>}
        {rewriteCount > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{rewriteCount} rewrite</span>}
        {newCount > 0 && <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700">{newCount} new</span>}
      </div>

      {/* Sections */}
      <div>
        <div className="text-xs font-medium text-gray-700 mb-1">
          Sections ({brief.sections.length})
        </div>
        <div className="space-y-1">
          {brief.sections.map((s: any) => (
            <div key={s.id} className="text-xs border rounded px-2 py-1.5 border-gray-200">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${ACTION_COLORS[s.action] || 'bg-gray-100 text-gray-600'}`}>
                  {s.action}
                </span>
                <span className="text-gray-400">{s.heading_level}</span>
                <span className="font-medium text-gray-800">{s.heading || '(Intro)'}</span>
                {s.target_word_count > 0 && (
                  <span className="text-gray-400 ml-auto">{s.target_word_count}w</span>
                )}
              </div>
              {s.instructions && s.action !== 'keep' && (
                <div className="text-gray-500 mt-0.5 pl-1">{s.instructions}</div>
              )}
              {s.research_needed && (
                <div className="text-blue-500 mt-0.5 pl-1">Research needed: {(s.research_queries || []).join(', ') || 'yes'}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      {brief.faq?.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700">FAQ ({brief.faq.length})</div>
          {brief.faq.map((f: any, i: number) => (
            <div key={i} className="text-xs text-gray-600">Q: {f.question}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export function RefreshBriefV2() {
  const { document } = useArticleContext();
  const hasAudit = Boolean((document as any)?.refresh?.audit);

  return (
    <StagePanel
      stageId="refresh_6_brief"
      isLlmStage
      title="Refresh: Brief"
      description="Synthesizes audit, SERP, competitors, and links into an actionable refresh plan."
      runDisabled={!hasAudit}
      runDisabledReason={!hasAudit ? 'Requires audit stage to complete first' : undefined}
      renderOutput={renderOutput}
    >
      <InputSummary />
    </StagePanel>
  );
}
